/**
 * EmojiPicker — Apr 2026
 *
 * Self-contained, dependency-free emoji picker for the chat composer
 * (and reusable elsewhere — message reactions, future profile emoji,
 * etc.). Renders a small panel with:
 *   • A search input (filters by keyword in the emoji's name list)
 *   • Category tabs at the bottom (smileys, animals, food, sport, etc.)
 *   • A scrollable grid of emoji buttons
 *
 * Why we built this instead of pulling in `emoji-mart`:
 *   - emoji-mart bundles ~250 KB compressed and adds a few hundred kB
 *     of CSS+image sprites. For a chat that already has a small,
 *     curated reaction set, that's overkill.
 *   - This module is ~12 KB total, no images (uses native unicode
 *     emoji rendered by the OS font), zero dependencies, and skinned
 *     to match Game Day's look.
 *   - The dataset is curated — about 200 of the most-used emoji
 *     across the major categories. That covers the 99% case for a
 *     sports team chat without forcing users to scroll past 1,800
 *     obscure ones (skin tone hand variations, regional flags,
 *     etc.).
 *
 * Props:
 *   onSelect(emoji)  — called with the picked emoji string
 *   onClose()        — called when the user dismisses (clicks outside)
 *   anchorAbove      — when true, picker positions above the trigger
 *                      (default true — the chat composer is at the
 *                      bottom of the screen, so we float upward).
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';

// Curated emoji dataset. Keyword arrays are intentionally generous so
// search hits feel forgiving — "happy", "smile", "joy" should all
// surface 😀. Categories are ordered by what a hockey/sports team
// would reach for first (smileys + sport + people > everything else).
const EMOJI_CATEGORIES = [
  {
    id: 'recent',
    icon: '🕒',
    label: 'Recent',
    items: [], // populated from localStorage at mount
  },
  {
    id: 'smileys',
    icon: '😀',
    label: 'Smileys',
    items: [
      { e: '😀', kw: ['happy', 'smile', 'grin'] },
      { e: '😃', kw: ['happy', 'smile', 'joy'] },
      { e: '😄', kw: ['happy', 'smile', 'laugh'] },
      { e: '😁', kw: ['grin', 'happy', 'smile'] },
      { e: '😆', kw: ['laugh', 'happy', 'lol'] },
      { e: '😅', kw: ['sweat', 'laugh', 'nervous'] },
      { e: '🤣', kw: ['rofl', 'laugh', 'lol', 'crying'] },
      { e: '😂', kw: ['joy', 'cry', 'laugh', 'lol'] },
      { e: '🙂', kw: ['smile', 'happy', 'slight'] },
      { e: '🙃', kw: ['upside', 'silly'] },
      { e: '😉', kw: ['wink', 'flirt'] },
      { e: '😊', kw: ['blush', 'happy', 'smile'] },
      { e: '😇', kw: ['angel', 'innocent', 'halo'] },
      { e: '🥰', kw: ['love', 'hearts', 'adore'] },
      { e: '😍', kw: ['love', 'hearts', 'eyes'] },
      { e: '🤩', kw: ['star', 'eyes', 'wow'] },
      { e: '😘', kw: ['kiss', 'love'] },
      { e: '😗', kw: ['kiss'] },
      { e: '😋', kw: ['yum', 'tasty', 'tongue'] },
      { e: '😛', kw: ['tongue', 'silly'] },
      { e: '😜', kw: ['wink', 'tongue', 'silly'] },
      { e: '🤪', kw: ['crazy', 'wild', 'silly'] },
      { e: '🤨', kw: ['suspicious', 'eyebrow'] },
      { e: '🧐', kw: ['monocle', 'thinking', 'inspect'] },
      { e: '🤓', kw: ['nerd', 'glasses'] },
      { e: '😎', kw: ['cool', 'sunglasses'] },
      { e: '🥳', kw: ['party', 'celebrate', 'birthday'] },
      { e: '😏', kw: ['smirk', 'sly'] },
      { e: '😒', kw: ['unamused', 'meh'] },
      { e: '😞', kw: ['sad', 'disappointed'] },
      { e: '😔', kw: ['pensive', 'sad'] },
      { e: '😟', kw: ['worried', 'concerned'] },
      { e: '😕', kw: ['confused'] },
      { e: '🙁', kw: ['frown', 'sad'] },
      { e: '☹️', kw: ['frown', 'sad'] },
      { e: '😣', kw: ['persevere', 'struggle'] },
      { e: '😖', kw: ['confounded', 'frustrated'] },
      { e: '😫', kw: ['tired', 'weary'] },
      { e: '😩', kw: ['weary', 'tired'] },
      { e: '🥺', kw: ['pleading', 'puppy'] },
      { e: '😢', kw: ['cry', 'sad', 'tear'] },
      { e: '😭', kw: ['sob', 'crying', 'sad'] },
      { e: '😤', kw: ['huff', 'frustrated'] },
      { e: '😠', kw: ['angry', 'mad'] },
      { e: '😡', kw: ['rage', 'angry', 'mad'] },
      { e: '🤬', kw: ['curse', 'swear', 'angry'] },
      { e: '🤯', kw: ['mind', 'blown', 'shocked'] },
      { e: '😳', kw: ['flushed', 'embarrassed'] },
      { e: '😱', kw: ['scream', 'shocked', 'fear'] },
      { e: '😨', kw: ['scared', 'fear'] },
      { e: '😰', kw: ['anxious', 'worried', 'sweat'] },
      { e: '😥', kw: ['relieved', 'sad', 'sweat'] },
      { e: '😓', kw: ['sweat', 'tired'] },
      { e: '🤗', kw: ['hug', 'embrace'] },
      { e: '🤔', kw: ['thinking', 'hmm'] },
      { e: '🤭', kw: ['oops', 'giggle'] },
      { e: '🤫', kw: ['shush', 'quiet'] },
      { e: '🤥', kw: ['lying', 'pinocchio'] },
      { e: '😶', kw: ['speechless', 'silent'] },
      { e: '😐', kw: ['neutral', 'meh'] },
      { e: '😑', kw: ['expressionless'] },
      { e: '😬', kw: ['grimace', 'awkward'] },
      { e: '🙄', kw: ['eyeroll', 'roll'] },
      { e: '😯', kw: ['hushed', 'oh'] },
      { e: '😦', kw: ['frown', 'open'] },
      { e: '😮', kw: ['oh', 'wow', 'open'] },
      { e: '🥱', kw: ['yawn', 'tired', 'bored'] },
      { e: '😴', kw: ['sleep', 'sleepy'] },
      { e: '🤤', kw: ['drool', 'desire'] },
      { e: '😪', kw: ['tired', 'sleepy'] },
      { e: '😵', kw: ['dizzy', 'shocked'] },
      { e: '🤐', kw: ['zipper', 'quiet'] },
      { e: '🥴', kw: ['woozy', 'drunk'] },
      { e: '🤢', kw: ['nausea', 'sick'] },
      { e: '🤮', kw: ['vomit', 'sick'] },
      { e: '🤧', kw: ['sneeze', 'sick'] },
      { e: '😷', kw: ['mask', 'sick'] },
      { e: '🤒', kw: ['fever', 'sick', 'thermometer'] },
      { e: '🤕', kw: ['injury', 'hurt', 'bandage'] },
    ],
  },
  {
    id: 'gestures',
    icon: '👍',
    label: 'Gestures',
    items: [
      { e: '👍', kw: ['thumbs', 'up', 'yes', 'good', 'like'] },
      { e: '👎', kw: ['thumbs', 'down', 'no', 'bad', 'dislike'] },
      { e: '👌', kw: ['ok', 'okay', 'good'] },
      { e: '🤌', kw: ['pinch', 'italian'] },
      { e: '🤏', kw: ['small', 'pinch'] },
      { e: '✌️', kw: ['peace', 'victory'] },
      { e: '🤞', kw: ['fingers', 'crossed', 'luck'] },
      { e: '🤟', kw: ['love', 'rock'] },
      { e: '🤘', kw: ['rock', 'metal'] },
      { e: '🤙', kw: ['call', 'shaka', 'hang', 'loose'] },
      { e: '👈', kw: ['point', 'left'] },
      { e: '👉', kw: ['point', 'right'] },
      { e: '👆', kw: ['point', 'up'] },
      { e: '👇', kw: ['point', 'down'] },
      { e: '☝️', kw: ['point', 'up', 'one'] },
      { e: '👋', kw: ['wave', 'hi', 'hello', 'bye'] },
      { e: '🤚', kw: ['hand', 'stop', 'raised'] },
      { e: '✋', kw: ['hand', 'stop', 'high', 'five'] },
      { e: '🖐️', kw: ['hand', 'splayed'] },
      { e: '🖖', kw: ['vulcan', 'spock', 'star', 'trek'] },
      { e: '👏', kw: ['clap', 'applause'] },
      { e: '🙌', kw: ['raise', 'hands', 'praise', 'celebrate'] },
      { e: '👐', kw: ['open', 'hands', 'hug'] },
      { e: '🤲', kw: ['palms', 'pray', 'beg'] },
      { e: '🙏', kw: ['pray', 'thanks', 'please'] },
      { e: '🤝', kw: ['handshake', 'deal', 'agreement'] },
      { e: '💪', kw: ['flex', 'muscle', 'strong'] },
      { e: '✊', kw: ['fist', 'power', 'punch'] },
      { e: '👊', kw: ['punch', 'fist', 'bump'] },
      { e: '🤛', kw: ['fist', 'left'] },
      { e: '🤜', kw: ['fist', 'right'] },
      { e: '👀', kw: ['eyes', 'look', 'see'] },
      { e: '🧠', kw: ['brain', 'smart', 'think'] },
      { e: '🦾', kw: ['mechanical', 'arm', 'strong'] },
      { e: '🦵', kw: ['leg', 'kick'] },
    ],
  },
  {
    id: 'sports',
    icon: '🏒',
    label: 'Sports',
    items: [
      { e: '🏒', kw: ['hockey', 'stick', 'ice'] },
      { e: '🥅', kw: ['goal', 'net', 'hockey'] },
      { e: '⛸️', kw: ['skate', 'ice', 'skating'] },
      { e: '🏆', kw: ['trophy', 'win', 'champion', 'cup'] },
      { e: '🥇', kw: ['gold', 'first', 'medal'] },
      { e: '🥈', kw: ['silver', 'second', 'medal'] },
      { e: '🥉', kw: ['bronze', 'third', 'medal'] },
      { e: '🎖️', kw: ['medal', 'military'] },
      { e: '🏅', kw: ['medal', 'award'] },
      { e: '⚽', kw: ['soccer', 'football', 'ball'] },
      { e: '🏀', kw: ['basketball', 'ball'] },
      { e: '🏈', kw: ['football', 'american', 'ball'] },
      { e: '⚾', kw: ['baseball', 'ball'] },
      { e: '🥎', kw: ['softball', 'ball'] },
      { e: '🎾', kw: ['tennis', 'ball'] },
      { e: '🏐', kw: ['volleyball', 'ball'] },
      { e: '🏉', kw: ['rugby', 'ball'] },
      { e: '🎱', kw: ['pool', 'billiards', 'eight'] },
      { e: '🏓', kw: ['ping', 'pong', 'paddle'] },
      { e: '🏸', kw: ['badminton', 'racket'] },
      { e: '🥊', kw: ['boxing', 'glove'] },
      { e: '🥋', kw: ['martial', 'arts', 'karate'] },
      { e: '⛳', kw: ['golf', 'flag'] },
      { e: '🎯', kw: ['target', 'bullseye', 'darts'] },
      { e: '🏃', kw: ['run', 'running'] },
      { e: '🏊', kw: ['swim', 'swimming'] },
      { e: '🚴', kw: ['bike', 'cycling'] },
      { e: '🏋️', kw: ['lifting', 'weight', 'gym'] },
      { e: '⛹️', kw: ['basketball', 'bouncing'] },
      { e: '🤸', kw: ['cartwheel', 'gymnastics'] },
    ],
  },
  {
    id: 'symbols',
    icon: '❤️',
    label: 'Symbols',
    items: [
      { e: '❤️', kw: ['heart', 'love', 'red'] },
      { e: '🧡', kw: ['heart', 'orange'] },
      { e: '💛', kw: ['heart', 'yellow'] },
      { e: '💚', kw: ['heart', 'green'] },
      { e: '💙', kw: ['heart', 'blue'] },
      { e: '💜', kw: ['heart', 'purple'] },
      { e: '🖤', kw: ['heart', 'black'] },
      { e: '🤍', kw: ['heart', 'white'] },
      { e: '🤎', kw: ['heart', 'brown'] },
      { e: '💔', kw: ['heart', 'broken', 'sad'] },
      { e: '❣️', kw: ['heart', 'exclamation'] },
      { e: '💕', kw: ['hearts', 'love'] },
      { e: '💞', kw: ['hearts', 'revolving'] },
      { e: '💓', kw: ['heart', 'beating'] },
      { e: '💗', kw: ['heart', 'growing'] },
      { e: '💖', kw: ['heart', 'sparkling'] },
      { e: '💘', kw: ['heart', 'arrow', 'cupid'] },
      { e: '💝', kw: ['heart', 'gift'] },
      { e: '✨', kw: ['sparkles', 'star', 'magic'] },
      { e: '🌟', kw: ['star', 'glowing'] },
      { e: '⭐', kw: ['star'] },
      { e: '💫', kw: ['dizzy', 'star'] },
      { e: '⚡', kw: ['lightning', 'bolt', 'zap'] },
      { e: '🔥', kw: ['fire', 'hot', 'lit', 'flame'] },
      { e: '💯', kw: ['hundred', 'perfect', 'score'] },
      { e: '✅', kw: ['check', 'yes', 'done'] },
      { e: '☑️', kw: ['check', 'box'] },
      { e: '✔️', kw: ['check', 'mark'] },
      { e: '❌', kw: ['x', 'no', 'cross', 'cancel'] },
      { e: '❎', kw: ['x', 'cross', 'box'] },
      { e: '⚠️', kw: ['warning', 'caution', 'alert'] },
      { e: '🚫', kw: ['no', 'prohibited', 'forbidden'] },
      { e: '❓', kw: ['question', 'what'] },
      { e: '❗', kw: ['exclamation', 'warning'] },
      { e: '💡', kw: ['idea', 'lightbulb'] },
      { e: '🎉', kw: ['party', 'celebrate', 'tada'] },
      { e: '🎊', kw: ['confetti', 'party'] },
      { e: '🎈', kw: ['balloon', 'party'] },
      { e: '🎁', kw: ['gift', 'present', 'birthday'] },
    ],
  },
  {
    id: 'food',
    icon: '🍕',
    label: 'Food',
    items: [
      { e: '🍕', kw: ['pizza', 'food'] },
      { e: '🍔', kw: ['burger', 'food'] },
      { e: '🍟', kw: ['fries', 'chips'] },
      { e: '🌭', kw: ['hotdog'] },
      { e: '🌮', kw: ['taco'] },
      { e: '🌯', kw: ['burrito'] },
      { e: '🥪', kw: ['sandwich'] },
      { e: '🥗', kw: ['salad', 'green'] },
      { e: '🍿', kw: ['popcorn'] },
      { e: '🍩', kw: ['donut', 'doughnut'] },
      { e: '🍪', kw: ['cookie'] },
      { e: '🎂', kw: ['cake', 'birthday'] },
      { e: '🍰', kw: ['cake', 'slice'] },
      { e: '🧁', kw: ['cupcake'] },
      { e: '🍦', kw: ['ice', 'cream', 'soft'] },
      { e: '🍫', kw: ['chocolate'] },
      { e: '🍬', kw: ['candy'] },
      { e: '🍭', kw: ['lollipop'] },
      { e: '🍻', kw: ['beer', 'cheers', 'drink'] },
      { e: '🍺', kw: ['beer', 'drink'] },
      { e: '🍷', kw: ['wine', 'drink'] },
      { e: '🥂', kw: ['champagne', 'cheers', 'celebrate'] },
      { e: '🍾', kw: ['champagne', 'bottle', 'celebrate'] },
      { e: '🥃', kw: ['whiskey', 'drink'] },
      { e: '🍸', kw: ['cocktail', 'martini'] },
      { e: '☕', kw: ['coffee', 'tea', 'hot'] },
      { e: '🍵', kw: ['tea', 'green'] },
      { e: '💧', kw: ['water', 'drop'] },
    ],
  },
  {
    id: 'animals',
    icon: '🐶',
    label: 'Animals',
    items: [
      { e: '🐶', kw: ['dog', 'puppy'] },
      { e: '🐱', kw: ['cat', 'kitten'] },
      { e: '🐭', kw: ['mouse'] },
      { e: '🐹', kw: ['hamster'] },
      { e: '🐰', kw: ['rabbit', 'bunny'] },
      { e: '🦊', kw: ['fox'] },
      { e: '🐻', kw: ['bear'] },
      { e: '🐼', kw: ['panda'] },
      { e: '🐨', kw: ['koala'] },
      { e: '🐯', kw: ['tiger'] },
      { e: '🦁', kw: ['lion'] },
      { e: '🐮', kw: ['cow'] },
      { e: '🐷', kw: ['pig'] },
      { e: '🐸', kw: ['frog'] },
      { e: '🐵', kw: ['monkey'] },
      { e: '🙈', kw: ['monkey', 'see'] },
      { e: '🙉', kw: ['monkey', 'hear'] },
      { e: '🙊', kw: ['monkey', 'speak'] },
      { e: '🐔', kw: ['chicken'] },
      { e: '🐧', kw: ['penguin'] },
      { e: '🐦', kw: ['bird'] },
      { e: '🦅', kw: ['eagle'] },
      { e: '🦆', kw: ['duck'] },
      { e: '🦉', kw: ['owl'] },
      { e: '🐺', kw: ['wolf'] },
      { e: '🐗', kw: ['boar', 'pig'] },
      { e: '🐎', kw: ['horse'] },
      { e: '🦄', kw: ['unicorn'] },
      { e: '🐝', kw: ['bee'] },
      { e: '🦋', kw: ['butterfly'] },
    ],
  },
  {
    id: 'travel',
    icon: '🚗',
    label: 'Travel',
    items: [
      { e: '🚗', kw: ['car'] },
      { e: '🚕', kw: ['taxi', 'cab'] },
      { e: '🚙', kw: ['suv', 'car'] },
      { e: '🚌', kw: ['bus'] },
      { e: '🚎', kw: ['trolley'] },
      { e: '🏎️', kw: ['race', 'car'] },
      { e: '🚓', kw: ['police', 'car'] },
      { e: '🚑', kw: ['ambulance'] },
      { e: '🚒', kw: ['fire', 'truck'] },
      { e: '🚐', kw: ['minibus'] },
      { e: '🚚', kw: ['truck'] },
      { e: '🚛', kw: ['truck'] },
      { e: '🚲', kw: ['bike', 'bicycle'] },
      { e: '🛵', kw: ['scooter'] },
      { e: '🏍️', kw: ['motorcycle'] },
      { e: '✈️', kw: ['plane', 'airplane', 'flight'] },
      { e: '🚀', kw: ['rocket', 'space'] },
      { e: '⛵', kw: ['boat', 'sailboat'] },
      { e: '🚂', kw: ['train', 'locomotive'] },
      { e: '🚆', kw: ['train'] },
      { e: '🌍', kw: ['earth', 'globe', 'world'] },
      { e: '🗺️', kw: ['map', 'world'] },
      { e: '⏰', kw: ['clock', 'alarm', 'time'] },
      { e: '⏳', kw: ['hourglass', 'time'] },
    ],
  },
];

// Flat list — for fast keyword search across categories.
const ALL_EMOJI = EMOJI_CATEGORIES.flatMap(c =>
  c.id === 'recent' ? [] : c.items.map(i => ({ ...i, cat: c.id }))
);

const RECENT_KEY = 'gameday-emoji-recent';
const RECENT_MAX = 24;

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch { return []; }
}

function pushRecent(emoji) {
  try {
    const cur = loadRecent().filter(e => e !== emoji);
    cur.unshift(emoji);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, RECENT_MAX)));
  } catch {}
}

export default function EmojiPicker({ onSelect, onClose, anchorAbove = true }) {
  const [search, setSearch] = useState('');
  // Active category, defaulting to "recent" if any are saved else smileys.
  const [recent, setRecent] = useState(() => loadRecent());
  const [activeCat, setActiveCat] = useState(() => loadRecent().length > 0 ? 'recent' : 'smileys');
  const wrapRef = useRef(null);

  // Click-outside to dismiss. Listens at the document level after a
  // microtask so the click that opened the picker doesn't immediately
  // close it.
  useEffect(() => {
    const t = setTimeout(() => {
      const onDocClick = (e) => {
        if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose?.();
      };
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('touchstart', onDocClick, { passive: true });
      return () => {
        document.removeEventListener('mousedown', onDocClick);
        document.removeEventListener('touchstart', onDocClick);
      };
    }, 0);
    return () => clearTimeout(t);
  }, [onClose]);

  // Visible items: when search has content, ignore categories and
  // filter the full list. Otherwise show the active category.
  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term) {
      return ALL_EMOJI.filter(i =>
        i.kw.some(k => k.includes(term)) || i.e === term
      ).slice(0, 200);
    }
    if (activeCat === 'recent') {
      return recent.map(e => ({ e, kw: [], cat: 'recent' }));
    }
    const cat = EMOJI_CATEGORIES.find(c => c.id === activeCat);
    return cat ? cat.items : [];
  }, [search, activeCat, recent]);

  const handlePick = (emoji) => {
    pushRecent(emoji);
    setRecent(loadRecent());
    onSelect?.(emoji);
    // Don't close the picker — let the user grab a few in a row if
    // they want. They can dismiss with the X button or by clicking
    // outside.
  };

  return (
    <div
      ref={wrapRef}
      className="emoji-picker"
      style={{
        position: 'absolute',
        ...(anchorAbove ? { bottom: 'calc(100% + 8px)' } : { top: 'calc(100% + 8px)' }),
        right: 0,
        width: 320,
        maxWidth: 'calc(100vw - 32px)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        boxShadow: '0 12px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
        padding: 10,
        zIndex: 50,
        display: 'flex', flexDirection: 'column',
        gap: 8,
      }}
    >
      {/* Search */}
      <div style={{ position: 'relative' }}>
        <Search size={13} style={{
          position: 'absolute', left: 9, top: '50%',
          transform: 'translateY(-50%)', color: 'var(--text-2)',
          pointerEvents: 'none',
        }} />
        <input
          autoFocus
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search emoji…"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '7px 10px 7px 28px',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            color: 'var(--text)',
            // 16px to avoid iOS zoom on focus.
            fontSize: 16,
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: 2,
        maxHeight: 220,
        overflowY: 'auto',
        // Slight inner padding so the scroll bar doesn't touch the
        // emoji column.
        padding: '2px 4px 2px 0',
      }}>
        {visibleItems.length === 0 && (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center', color: 'var(--text-2)',
            fontSize: 12, padding: 16,
          }}>
            {search.trim() ? `No emoji match "${search}"` : 'No emoji yet — pick one to start!'}
          </div>
        )}
        {visibleItems.map((it, i) => (
          <button
            key={`${it.e}_${i}`}
            type="button"
            onClick={() => handlePick(it.e)}
            title={it.kw?.[0] || it.e}
            style={{
              width: 34, height: 34,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 22,
              lineHeight: 1,
              padding: 0,
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {it.e}
          </button>
        ))}
      </div>

      {/* Category tabs — hidden during active search */}
      {!search.trim() && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          gap: 2,
        }}>
          {EMOJI_CATEGORIES.map(cat => {
            // Hide the Recent tab when there are no saved recents.
            if (cat.id === 'recent' && recent.length === 0) return null;
            const active = activeCat === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCat(cat.id)}
                title={cat.label}
                style={{
                  flex: 1,
                  padding: '6px 0',
                  background: active ? 'var(--ice-a8, rgba(0,212,255,0.08))' : 'transparent',
                  border: `1px solid ${active ? 'var(--ice-a30, rgba(0,212,255,0.30))' : 'transparent'}`,
                  borderRadius: 7,
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  transition: 'background 0.12s',
                }}
              >
                {cat.icon}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
